import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';
import wirelessApi from '../../services/wirelessApi';
import offensiveApi from '../../services/offensiveApi';
import wirelessSurveySocket from '../../services/wirelessSurveySocket';
import ChannelSpectrum from '../../components/wireless/ChannelSpectrum';
import ChannelOccupancy from '../../components/wireless/ChannelOccupancy';
import SignalTimeline from '../../components/wireless/SignalTimeline';
import NetworkTable from '../../components/wireless/NetworkTable';
import TestCatalogue from '../../components/wireless/TestCatalogue';
import CampaignPanel from '../../components/wireless/CampaignPanel';

/** How many signal readings to keep per AP. At one frame every two seconds this
 *  is three minutes of history — long enough to walk a floor and watch a signal
 *  climb, which is how a rogue AP is actually located. */
const HISTORY_DEPTH = 90;
const FRAME_INTERVAL_MS = 2000;

/** Tracking every network would reproduce the spectrum view's mistake: at a
 *  hundred series the chart is a texture, not a measurement. */
const MAX_TRACKED = 8;

const STATE_STYLES = {
  streaming: { dot: 'bg-emerald-500', text: 'text-emerald-400', label: 'Live' },
  connected: { dot: 'bg-indigo-500', text: 'text-indigo-400', label: 'Connected' },
  degraded: { dot: 'bg-amber-500', text: 'text-amber-400', label: 'Degraded' },
  disconnected: { dot: 'bg-slate-600', text: 'text-slate-500', label: 'Disconnected' },
};

function WirelessTesting() {
  // The engagement comes from the route. It used to be a dropdown listing every
  // offensive engagement, which meant a survey could be pointed at a red team
  // by accident and made the page a console rather than a workspace.
  const { engagementId: routeEngagementId } = useParams();
  const engagementId = routeEngagementId ? Number(routeEngagementId) : null;

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('survey');

  const [engagement, setEngagement] = useState(null);

  const [snapshot, setSnapshot] = useState(null);
  const [connState, setConnState] = useState('disconnected');
  const [connDetail, setConnDetail] = useState(null);
  const [streamError, setStreamError] = useState(null);
  const [agentInfo, setAgentInfo] = useState(null);

  const [targets, setTargets] = useState([]);
  const [tests, setTests] = useState([]);
  const [runs, setRuns] = useState([]);
  const [roe, setRoe] = useState({});
  const [catalogueBusy, setCatalogueBusy] = useState(false);
  const [highlight, setHighlight] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tracked, setTracked] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [chartView, setChartView] = useState('timeline');

  // Signal history per BSSID, kept client-side: it is a property of watching,
  // not something worth a database write every two seconds.
  const historyRef = useRef({});
  const [history, setHistory] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const all = await offensiveApi.getEngagements();
        setEngagement((all || []).find((e) => e.id === engagementId) || null);
      } catch (err) {
        console.error('Could not load engagements', err);
      }
      try {
        setTests(await wirelessApi.getTests());
      } catch (err) {
        console.error('Could not load the wireless catalogue', err);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshTargets = useCallback(async (id) => {
    if (!id) return;
    try {
      setTargets(await wirelessApi.getTargets(id));
    } catch (err) {
      console.error('Could not load targets', err);
    }
  }, []);

  // Runs and RoE come from the API rather than from the engagement payload:
  // the server is what enforces authorisation, so the panel must show what the
  // server believes, not what a cached scope object said.
  const refreshRuns = useCallback(async (id) => {
    if (!id) return;
    try {
      setRuns(await wirelessApi.getRuns(id));
    } catch (err) {
      console.error('Could not load test runs', err);
    }
  }, []);

  const refreshRoe = useCallback(async (id) => {
    if (!id) return;
    try {
      setRoe(await wirelessApi.getRoE(id));
    } catch (err) {
      console.error('Could not load the wireless rules of engagement', err);
    }
  }, []);

  useEffect(() => {
    if (!engagementId) return;
    refreshTargets(engagementId);
    refreshRuns(engagementId);
    refreshRoe(engagementId);

    historyRef.current = {};
    setHistory({});
    setSnapshot(null);
    setStreamError(null);
    setTracked([]);

    const offSurvey = wirelessSurveySocket.on('survey', (frame) => {
      setSnapshot(frame);
      const next = { ...historyRef.current };
      (frame.access_points || []).forEach((ap) => {
        const series = next[ap.bssid] ? [...next[ap.bssid]] : [];
        series.push(ap.signal_dbm);
        if (series.length > HISTORY_DEPTH) series.shift();
        next[ap.bssid] = series;
      });
      historyRef.current = next;
      setHistory(next);
    });
    const offStatus = wirelessSurveySocket.on('status', (s) => {
      setConnState(s.state);
      setConnDetail(s.detail || null);
      if (s.agent) setAgentInfo(s.agent);
    });
    const offError = wirelessSurveySocket.on('error', (e) => setStreamError(e.error));

    wirelessSurveySocket.connect(engagementId);

    // Targets are written by the stream at a coarser cadence than frames, so
    // re-read periodically rather than after every frame.
    const poll = setInterval(() => refreshTargets(engagementId), 15000);

    return () => {
      offSurvey();
      offStatus();
      offError();
      clearInterval(poll);
      wirelessSurveySocket.disconnect();
    };
  }, [engagementId, refreshTargets, refreshRuns, refreshRoe]);

  const targetsByBssid = targets.reduce((acc, t) => {
    acc[t.bssid] = t;
    return acc;
  }, {});

  // Selecting a network tracks it. Highlighting and tracking are the same
  // gesture on purpose: an analyst who has picked a row out of a hundred wants
  // to watch it, and making them find a second control to say so is friction
  // in the middle of the one task this view exists for.
  const handleSelect = (bssid) => {
    setHighlight(bssid);
    setTracked((prev) => (
      prev.includes(bssid) ? prev : [...prev, bssid].slice(-MAX_TRACKED)
    ));
  };

  const handleUntrack = (bssid) =>
    setTracked((prev) => prev.filter((b) => b !== bssid));

  const handleAuthorize = async (ap, authorized) => {
    const stored = targetsByBssid[ap.bssid];
    if (!stored) return;
    try {
      const updated = await wirelessApi.updateTarget(stored.id, { authorized });
      setTargets((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
    } catch (err) {
      console.error('Could not update target', err);
    }
  };

  // --- Catalogue actions ----------------------------------------------------
  // Every one of these re-reads the runs afterwards rather than patching state
  // locally: the server decides status (a refused run comes back as
  // not_authorized, not as an error), so the client must not guess at it.
  const withCatalogueBusy = async (fn) => {
    setCatalogueBusy(true);
    try {
      await fn();
      await refreshRuns(engagementId);
    } catch (err) {
      console.error('Catalogue action failed', err);
      setStreamError(err.response?.data?.detail || err.message);
    } finally {
      setCatalogueBusy(false);
    }
  };

  const handleEvaluateRecon = () =>
    withCatalogueBusy(() => wirelessApi.evaluateRecon(engagementId));

  const handleCreateRun = (test) =>
    withCatalogueBusy(() => wirelessApi.createRun(engagementId, { test_id: test.id }));

  const handleUpdateRun = (run, changes) =>
    withCatalogueBusy(() => wirelessApi.updateRun(run.id, changes));

  const handlePromote = (run) =>
    withCatalogueBusy(() => wirelessApi.promoteRun(run.id));

  const [riskDraft, setRiskDraft] = useState(null);

  // A run that produced a finding is one that needs an Annex B estimate.
  const promotedCount = useMemo(
    () => runs.filter((r) => r.finding_id).length,
    [runs],
  );

  /**
   * Draft Annex B's estimation for the findings promoted here.
   *
   * Promotion writes a finding with no risk estimate at all — amenazas,
   * controles mitigantes and their narratives are analyst judgements, so
   * nothing invents them — and Annex B then prints N/D on every row. The
   * generator drafts them from the findings; the analyst reviews the levels
   * before the report is generated, which is the point of it being a draft.
   *
   * Reachable from here because this is where wireless findings are made. It
   * already existed on the offensive engagements table, which is not a page an
   * operator running a WLAN assessment ever opens — so it never ran, and every
   * wireless report shipped with an empty Annex B.
   */
  const handleDraftRiskAnalysis = () =>
    withCatalogueBusy(async () => {
      setRiskDraft(null);
      const result = await offensiveApi.draftFindingsRiskAnalysis(engagementId);
      setRiskDraft(result);
    });

  const handleToggleRoE = (key, value) =>
    withCatalogueBusy(async () => {
      setRoe(await wirelessApi.updateRoE(engagementId, { [key]: value }));
    });

  /**
   * Open a run against a target and hand it to the agent.
   *
   * Two calls rather than one because a refused run is a recorded outcome, not
   * an error: if the server comes back not_authorized there is nothing to
   * dispatch, and the refusal is already saved with its reason.
   */
  const handleStop = (run) => withCatalogueBusy(() => wirelessApi.stopRun(run.id));

  const handleDispatch = (test, targetId) =>
    withCatalogueBusy(async () => {
      const run = await wirelessApi.createRun(engagementId, {
        test_id: test.id,
        target_id: targetId,
      });
      if (run.status === 'not_authorized') return;
      await wirelessApi.dispatchRun(run.id);
    });

  /**
   * One active scan. Reads what the passive survey cannot — 802.11w, the AKM
   * suite, WPS, and the SSIDs of networks that hide them — and folds it into
   * the targets. Four catalogue tests are decidable only from this, and until
   * one has been run they correctly report that nobody looked.
   */
  const runScan = async () => {
    if (!engagementId) return;
    setScanning(true);
    setStreamError(null);
    try {
      const result = await wirelessApi.runScan(engagementId);
      setScanResult(result);
      await refreshTargets(engagementId);
    } catch (err) {
      setStreamError(err.response?.data?.detail || err.message);
    } finally {
      setScanning(false);
    }
  };

  const controlSurvey = async (action) => {
    if (!engagementId) return;
    setBusy(true);
    setStreamError(null);
    try {
      if (action === 'start') await wirelessApi.startSurvey(engagementId);
      else await wirelessApi.stopSurvey(engagementId);
    } catch (err) {
      setStreamError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  // Memoised: a new array identity every render would re-run the seeding
  // effect below on every frame.
  const aps = useMemo(() => snapshot?.access_points || [], [snapshot]);
  const totals = snapshot?.totals || {};
  const surveyRunning = snapshot?.running;
  const state = STATE_STYLES[connState] || STATE_STYLES.disconnected;

  // Collect dispatched results. Polling rather than a socket because a test is
  // minutes to half an hour long and its state lives in the database and on the
  // agent — nothing is lost if this page is closed and reopened mid-capture.
  useEffect(() => {
    const running = runs.filter((r) => r.status === 'running');
    if (!running.length) return undefined;

    const timer = setInterval(async () => {
      try {
        const collected = await Promise.all(running.map((r) => wirelessApi.pollRun(r.id)));
        setRuns((prev) => prev.map((r) => collected.find((c) => c.id === r.id) || r));
      } catch (err) {
        // An unreachable agent leaves the run running server-side; retry.
        console.error('Could not poll dispatched runs', err);
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [runs]);

  // Seed the timeline so it is never empty on arrival: the authorised targets
  // if any have been promoted, otherwise the strongest few — which in practice
  // are the networks in this room, and therefore the ones being assessed.
  useEffect(() => {
    if (tracked.length || !aps.length) return;
    const authorized = targets.filter((t) => t.authorized).map((t) => t.bssid);
    const seed = authorized.length
      ? aps.filter((ap) => authorized.includes(ap.bssid)).map((ap) => ap.bssid)
      : [...aps]
          .sort((a, b) => (b.signal_dbm ?? -100) - (a.signal_dbm ?? -100))
          .map((ap) => ap.bssid);
    if (seed.length) setTracked(seed.slice(0, 5));
  }, [aps, targets, tracked.length]);

  const Stat = ({ value, label, tone = 'text-slate-200' }) => (
    <div className="flex items-baseline gap-1.5">
      <span className={`text-lg font-semibold tabular-nums ${tone}`}>{value}</span>
      <span className="text-[11px] text-slate-500">{label}</span>
    </div>
  );

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-[96rem] mx-auto">

            {/* Header row */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl text-slate-100 font-bold">Wireless Testing</h1>
                <p className="text-sm text-slate-500 mt-1">
                  802.11 assessment against NIST SP 800-153, SP 800-97 and PCI DSS
                </p>
              </div>

              <div className="flex items-center gap-3">
                {/* Which engagement, not a choice of engagement: the page is
                    reached from the wireless engagement list. */}
                <span className="text-sm text-slate-300">
                  {engagement
                    ? `${engagement.customer?.short_name || engagement.customer?.name || 'Engagement'} — ${engagement.engagement_code || engagement.id}`
                    : `Engagement ${engagementId ?? ''}`}
                </span>

                <button
                  onClick={runScan}
                  disabled={scanning || !engagementId}
                  title="Sends directed probes. Reads 802.11w state, the authentication suite, WPS and hidden SSIDs — none of which a passive capture reports. Briefly interrupts the survey."
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-700 text-white hover:bg-slate-600 transition-colors disabled:opacity-50"
                >
                  {scanning ? 'Scanning…' : 'Active scan'}
                </button>

                <button
                  onClick={() => controlSurvey(surveyRunning ? 'stop' : 'start')}
                  disabled={busy || !engagementId}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                    surveyRunning
                      ? 'bg-slate-700 text-white hover:bg-slate-600'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                >
                  {busy ? 'Working…' : surveyRunning ? 'Stop survey' : 'Start survey'}
                </button>
              </div>
            </div>

            {/* Status strip — compact on purpose; the visualisation is the point */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 rounded-lg bg-slate-900/50 border border-slate-800 mb-6">
              <span className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${state.dot} ${connState === 'streaming' ? 'animate-pulse' : ''}`} />
                <span className={`text-sm font-medium ${state.text}`}>{state.label}</span>
              </span>

              {agentInfo && (
                <span className="text-[11px] text-slate-500 font-mono">
                  {agentInfo.name} · {agentInfo.vpn_ip}
                </span>
              )}
              {snapshot?.interface && (
                <span className="text-[11px] text-slate-500 font-mono">{snapshot.interface}</span>
              )}

              <span className="h-4 w-px bg-slate-800 hidden sm:block" />

              <Stat value={totals.access_points ?? 0} label="networks" />
              <Stat value={totals.clients ?? 0} label="clients" />
              <Stat
                value={totals.open_networks ?? 0}
                label="open"
                tone={totals.open_networks ? 'text-amber-400' : 'text-slate-200'}
              />
              <Stat value={totals.enterprise_networks ?? 0} label="enterprise" />
              <Stat value={totals.hidden_networks ?? 0} label="hidden" />

              {snapshot?.at && (
                <span className="ml-auto text-[11px] text-slate-600 tabular-nums">
                  frame {snapshot.frame} · {new Date(snapshot.at).toLocaleTimeString('en-GB')}
                </span>
              )}
            </div>

            {scanResult && (
              <div className="mb-6 p-3 rounded-lg bg-slate-900/50 border border-slate-800 text-xs text-slate-300 flex flex-wrap items-center gap-x-5 gap-y-1">
                <span className="font-medium text-slate-200">Scan complete</span>
                <span>{scanResult.access_points} access points</span>
                {scanResult.pmf_required > 0 && <span>{scanResult.pmf_required} enforce 802.11w</span>}
                {/* The third state, and the reason the counts did not add up.
                    802.11w capable but not required protects clients that
                    support it and leaves every other client deauthenticable —
                    a partial control, not a lesser version of enforced. */}
                {scanResult.pmf_optional > 0 && (
                  <span className="text-amber-400">
                    {scanResult.pmf_optional} offer 802.11w without requiring it
                  </span>
                )}
                {scanResult.pmf_disabled > 0 && (
                  <span className="text-amber-400">{scanResult.pmf_disabled} without 802.11w</span>
                )}
                {/* Open networks carry no RSN, so they are in none of the PMF
                    buckets — the parser leaves pmf null for them. Omitting the
                    count made the totals unreconcilable and, more to the point,
                    hid an unencrypted WLAN in a bank's estate behind a subtraction. */}
                {scanResult.open > 0 && (
                  <span className="text-red-400">{scanResult.open} open (no encryption)</span>
                )}
                {scanResult.wps > 0 && <span className="text-amber-400">{scanResult.wps} advertise WPS</span>}
                {scanResult.hidden > 0 && <span>{scanResult.hidden} hidden SSID</span>}
                {scanResult.wpa3 > 0 && <span className="text-emerald-400">{scanResult.wpa3} WPA3</span>}
                {scanResult.disclosed_hidden_ssid > 0 && (
                  <span className="text-red-400">
                    {scanResult.disclosed_hidden_ssid} hidden SSID disclosed under probe
                  </span>
                )}
                <button
                  onClick={() => setScanResult(null)}
                  className="ml-auto text-slate-600 hover:text-slate-400"
                >
                  dismiss
                </button>
              </div>
            )}

            {(streamError || connDetail) && (
              <div className="mb-6 p-3 rounded-lg bg-amber-950/20 border border-amber-500/30 text-xs text-slate-300">
                {streamError || `Frame dropped: ${connDetail}`}
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2 mb-6 border-b border-slate-800">
              {[
                { id: 'survey', label: 'Live Survey' },
                { id: 'catalogue', label: `Test Catalogue (${tests.length})` },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-400'
                      : 'border-transparent text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'survey' ? (
              <div className="space-y-6">
                <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-800">
                  <div className="flex justify-end mb-2">
                    <div className="inline-flex rounded-md border border-slate-800 overflow-hidden">
                      {[
                        { id: 'timeline', label: 'Timeline' },
                        { id: 'spectrum', label: 'Spectrum' },
                      ].map((view) => (
                        <button
                          key={view.id}
                          onClick={() => setChartView(view.id)}
                          className={`px-3 py-1 text-[11px] font-medium transition-colors ${
                            chartView === view.id
                              ? 'bg-slate-800 text-slate-200'
                              : 'text-slate-500 hover:text-slate-300'
                          }`}
                        >
                          {view.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {chartView === 'timeline' ? (
                    <div className="space-y-6">
                      <SignalTimeline
                        history={history}
                        accessPoints={aps}
                        tracked={tracked}
                        onUntrack={handleUntrack}
                        depth={HISTORY_DEPTH}
                        intervalMs={FRAME_INTERVAL_MS}
                      />
                      <ChannelOccupancy accessPoints={aps} />
                    </div>
                  ) : (
                    <ChannelSpectrum
                      accessPoints={aps}
                      highlightBssid={highlight}
                      onSelect={handleSelect}
                    />
                  )}
                </div>

                <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-800">
                  <NetworkTable
                    accessPoints={aps}
                    targetsByBssid={targetsByBssid}
                    history={history}
                    highlightBssid={highlight}
                    onSelect={handleSelect}
                    onAuthorize={handleAuthorize}
                  />
                </div>

                <p className="text-[11px] text-slate-600 leading-relaxed">
                  Coverage is bounded by the radio range of the host machine. What is surveyed is
                  the RF neighbourhood of a single location, not the customer’s whole estate —
                  covering more means relocating the host or deploying another.
                </p>
              </div>
            ) : (
              <div>
                {/* Above the catalogue: the unattended run is the thing an
                    operator reaches for first, and the per-test controls below
                    are what they fall back to for the rest. */}
                {/* After promotion, before generation: the one step between a
                    finding existing and Annex B having anything to say. */}
                {promotedCount > 0 && (
                  <div className="mb-6 rounded-lg bg-slate-900/50 border border-slate-800 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h2 className="text-sm font-semibold text-slate-200">Annex B estimation</h2>
                        <p className="text-[11px] text-slate-500 mt-0.5 max-w-xl">
                          {promotedCount} finding{promotedCount === 1 ? '' : 's'} promoted.
                          Amenazas, controles mitigantes and their narratives are analyst
                          estimates — nothing fills them automatically, so Annex B prints
                          N/D until this is drafted and reviewed.
                        </p>
                      </div>
                      <button
                        onClick={handleDraftRiskAnalysis}
                        disabled={catalogueBusy}
                        className="px-3 py-1.5 rounded-md border border-slate-700 text-slate-300 text-[11px] hover:bg-slate-800 disabled:opacity-50 shrink-0"
                        title="Drafts the estimation for findings that have none. Existing estimations are left alone."
                      >
                        {catalogueBusy ? 'Drafting…' : 'Draft risk analysis'}
                      </button>
                    </div>
                    {riskDraft && (
                      <p className="text-[11px] text-slate-400 mt-2">
                        {riskDraft.drafted ?? 0} drafted, {riskDraft.skipped ?? 0} already
                        estimated. Review the levels in the finding editor before generating —
                        a drafted estimate is a starting point, not a judgement.
                      </p>
                    )}
                  </div>
                )}

                <CampaignPanel
                  engagementId={engagementId}
                  onChanged={() => {
                    refreshRuns(engagementId);
                    refreshTargets(engagementId);
                  }}
                />
                <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-800">
                <TestCatalogue
                  tests={tests}
                  runs={runs}
                  roe={roe}
                  targets={targets}
                  busy={catalogueBusy}
                  onEvaluateRecon={handleEvaluateRecon}
                  onCreateRun={handleCreateRun}
                  onUpdateRun={handleUpdateRun}
                  onPromote={handlePromote}
                  onToggleRoE={handleToggleRoE}
                  onDispatch={handleDispatch}
                  onStop={handleStop}
                />
              </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default WirelessTesting;
